/**
 * AppSidebar.jsx — Phase 2 App Shell (Jul 2026) — Redesigned
 *
 * Simplified left pane driven ENTIRELY by the Phase 1 canonical module
 * catalog. Front-end reorganises the backend catalog groups into the
 * redesigned sidebar sections:
 *
 *   1. OVERVIEW  (renamed from "Home", minus Announcements)
 *   2. PEOPLE
 *   3. ATTENDANCE
 *   4. ORGANIZATION  (new — Announcements moved here)
 *   5. SELFIE AUDIT
 *   6. TIME OFF
 *   7. EXPENSES
 *   8. PAYROLL & FINANCE
 *   9. COMPLIANCE  (Audit Log + Tickets moved from Settings)
 *  10. REPORTS
 *
 * Settings link is in the footer; the Settings catalog group is hidden.
 *
 * Also owns:
 *   • In-nav search (typeahead + keyboard nav)
 *   • Pinning (localStorage per user, max 8)
 *   • Per-group collapse persistence (localStorage `ankastra_sidebar_groups_v3`)
 *   • Compact / expanded toggle (localStorage `ankastra_sidebar_compact_v1`)
 *   • Platform mode (SuperAdmin / SuperAccountant / SuperSupport without
 *     a company selected) renders a purpose-built platform nav.
 *
 * Consumes `ModuleActivationContext` for the catalog + active-key set.
 * Filters items whose module is deactivated for the current tenant.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronDown, Search, Pin, PinOff, PanelLeftClose, PanelLeftOpen,
  Building2, Users as UsersIcon, Ticket, ServerCog, Megaphone, ArrowUpCircle, HardDrive, Shield,
  Settings,
} from 'lucide-react';

import { authService } from '../../services/authService';
import { useModuleActivation } from './ModuleActivationContext';
import { iconForModule } from '../../config/moduleIcons';

const SIDEBAR_COLLAPSED_KEY = 'ankastra_sidebar_groups_v3';
const SIDEBAR_COMPACT_KEY   = 'ankastra_sidebar_compact_v1';
const SIDEBAR_PINS_KEY      = 'ankastra_sidebar_pins_v1';
const MAX_PINS = 8;

// First-visit defaults — expand daily-use groups, collapse the rest.
const DEFAULT_EXPANDED = new Set(['overview', 'people', 'attendance', 'timeoff']);

// ── Sidebar group display configuration ─────────────────────────────
// This maps backend catalog group keys to the redesigned sidebar labels
// and defines the new order. Modules are reassigned as needed.
const SIDEBAR_GROUP_ORDER = [
  'overview', 'people', 'attendance', 'organization',
  'selfie', 'timeoff', 'expenses', 'payroll', 'compliance', 'reports',
];

const SIDEBAR_GROUP_LABELS = {
  overview:     'OVERVIEW',
  people:       'PEOPLE',
  attendance:   'ATTENDANCE',
  organization: 'ORGANIZATION',
  selfie:       'SELFIE AUDIT',
  timeoff:      'TIME OFF',
  expenses:     'EXPENSES',
  payroll:      'PAYROLL & FINANCE',
  compliance:   'COMPLIANCE',
  reports:      'REPORTS',
};

// ── Platform sidebar (SA-no-company) ────────────────────────────────
function platformItemsFor(role) {
  const items = [];
  if (role === 'SuperAdmin')       items.push({ key: 'platform-overview', label: 'Overview', path: '/super-admin', Icon: Shield });
  items.push({ key: 'platform-companies', label: 'Companies', path: '/companies', Icon: Building2 });
  if (role === 'SuperAdmin')       items.push({ key: 'platform-users', label: 'Users', path: '/users', Icon: UsersIcon });
  items.push({ key: 'platform-tickets', label: 'Tickets', path: '/tickets', Icon: Ticket });
  if (role === 'SuperAdmin') {
    items.push({ key: 'platform-system', label: 'System Metrics', path: '/system', Icon: ServerCog });
    items.push({ key: 'platform-broadcast', label: 'Marketing Broadcast', path: '/marketing-broadcast', Icon: Megaphone });
    items.push({ key: 'platform-adv-migrate', label: 'Advanced Features Migration', path: '/super-admin/advanced-features-migration', Icon: ArrowUpCircle });
    items.push({ key: 'platform-hw-logs', label: 'Hardware Logs (All Tenants)', path: '/super-admin/hardware-logs', Icon: HardDrive });
  }
  return items;
}

function readPins(userId) {
  try {
    const raw = localStorage.getItem(SIDEBAR_PINS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.[userId]) ? parsed[userId] : [];
  } catch { return []; }
}

function writePins(userId, pins) {
  try {
    const raw = localStorage.getItem(SIDEBAR_PINS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[userId] = pins;
    localStorage.setItem(SIDEBAR_PINS_KEY, JSON.stringify(parsed));
  } catch { /* no-op */ }
}

/**
 * Score a candidate for the search query.
 */
function searchScore(label, groupLabel, needle) {
  const n = (needle || '').trim().toLowerCase();
  if (!n) return 0;
  const l = (label || '').toLowerCase();
  const g = (groupLabel || '').toLowerCase();
  if (l.startsWith(n)) return 100;
  if (l.includes(n))   return 50;
  if (g.startsWith(n)) return 30;
  if (g.includes(n))   return 20;
  let hi = 0;
  for (let i = 0; i < n.length; i++) {
    const idx = l.indexOf(n[i], hi);
    if (idx === -1) return -1;
    hi = idx + 1;
  }
  return 5;
}

/**
 * Transform backend catalog groups into the redesigned sidebar groups.
 * - Rename home → overview, remove announcements from it
 * - Create organization group with announcements
 * - Move tickets from settings to compliance
 * - Drop settings group entirely (settings link is in footer)
 */
function reorganizeCatalog(catalogGroups, activeKeys, userRole) {
  // 1. Build a flat module map from all catalog groups
  const allModules = [];
  for (const g of catalogGroups) {
    for (const m of (g.modules || [])) {
      allModules.push({ ...m, originalGroup: g.groupKey });
    }
  }

  // 2. Assign modules to new sidebar groups
  const groupBuckets = {};
  for (const key of SIDEBAR_GROUP_ORDER) groupBuckets[key] = [];

  for (const m of allModules) {
    // Role filter
    if (Array.isArray(m.roles) && userRole && !m.roles.includes(userRole)) continue;
    // Active-key filter
    if (!activeKeys.has(m.key)) continue;

    // Reassignment rules:
    if (m.key === 'announcements') {
      groupBuckets['organization'].push(m);
    } else if (m.key === 'tickets') {
      groupBuckets['compliance'].push(m);
    } else if (m.key === 'settings') {
      // Settings goes to footer — skip from catalog groups
      continue;
    } else if (m.originalGroup === 'home') {
      groupBuckets['overview'].push(m);
    } else if (m.originalGroup === 'settings') {
      // Any remaining settings-group items go to compliance
      groupBuckets['compliance'].push(m);
    } else if (groupBuckets[m.originalGroup]) {
      groupBuckets[m.originalGroup].push(m);
    }
    // else: module belongs to a group not in our redesigned list — skip
  }

  // 3. Build final group array with icons
  const result = [];
  for (const key of SIDEBAR_GROUP_ORDER) {
    const modules = groupBuckets[key];
    if (modules.length === 0) continue;
    result.push({
      groupKey: key,
      groupLabel: SIDEBAR_GROUP_LABELS[key] || key.toUpperCase(),
      modules: modules.map(m => ({ ...m, Icon: iconForModule(m.key) })),
    });
  }
  return result;
}

export default function AppSidebar({
  isMobileOpen = false,
  onMobileClose = () => {},
  onOpenCommandPalette = () => {},
  isPlatformScope = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const userId = user?.id ?? user?.email ?? 'anon';

  const { catalogGroups, activeKeys, loading: modLoading } = useModuleActivation();

  // ── State: compact, collapsed groups, pins, search ────────────────
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COMPACT_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COMPACT_KEY, compact ? '1' : '0'); } catch { /* no-op */ }
  }, [compact]);

  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* fall through */ }
    const defaults = {};
    SIDEBAR_GROUP_ORDER.forEach(k => {
      if (!DEFAULT_EXPANDED.has(k)) defaults[k] = true;
    });
    return defaults;
  });
  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
  };

  const [pins, setPins] = useState(() => readPins(userId));
  useEffect(() => { setPins(readPins(userId)); }, [userId]);
  const togglePin = (moduleKey) => {
    setPins(prev => {
      const isPinned = prev.includes(moduleKey);
      let next;
      if (isPinned) {
        next = prev.filter(k => k !== moduleKey);
      } else {
        if (prev.length >= MAX_PINS) {
          toast.error(`You can pin at most ${MAX_PINS} items. Unpin one first.`);
          return prev;
        }
        next = [...prev, moduleKey];
      }
      writePins(userId, next);
      return next;
    });
  };

  const [query, setQuery] = useState('');
  const [activeSearchIdx, setActiveSearchIdx] = useState(-1);
  const searchRef = useRef(null);

  // Global "/" shortcut → focus the sidebar search.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const editing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (editing) return;
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Build reorganized items from the catalog ───────────────────────
  const items = useMemo(
    () => reorganizeCatalog(catalogGroups, activeKeys, user?.role),
    [catalogGroups, activeKeys, user?.role]
  );

  const flatItems = useMemo(
    () => items.flatMap(g => g.modules.map(m => ({ ...m, groupLabel: g.groupLabel }))),
    [items]
  );

  // Check if Settings module is active (for the footer link)
  const settingsActive = activeKeys.has('settings');

  // Pinned items: intersect user's pins with currently-visible items.
  const pinnedItems = useMemo(() => {
    const byKey = new Map(flatItems.map(m => [m.key, m]));
    return pins.map(k => byKey.get(k)).filter(Boolean);
  }, [pins, flatItems]);

  // Search results (only when query is non-empty). Ranked by searchScore.
  const searchResults = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const scored = [];
    for (const m of flatItems) {
      const s = searchScore(m.label, m.groupLabel, q);
      if (s > 0) scored.push({ m, s });
    }
    scored.sort((a, b) => b.s - a.s);
    return scored.slice(0, 10).map(x => x.m);
  }, [query, flatItems]);
  useEffect(() => { setActiveSearchIdx(searchResults.length > 0 ? 0 : -1); }, [searchResults.length]);

  const onSearchKey = (e) => {
    if (searchResults.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveSearchIdx(i => Math.min(searchResults.length - 1, (i < 0 ? 0 : i + 1))); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveSearchIdx(i => Math.max(0, (i < 0 ? 0 : i - 1))); }
    else if (e.key === 'Enter')     {
      const target = searchResults[activeSearchIdx >= 0 ? activeSearchIdx : 0];
      if (target) { setQuery(''); navigate(target.route); onMobileClose(); }
    } else if (e.key === 'Escape')  {
      setQuery('');
      e.currentTarget.blur();
    }
  };

  // Auto-expand the group that owns the active route.
  useEffect(() => {
    for (const g of items) {
      const hit = g.modules.find(m => location.pathname === m.route || location.pathname.startsWith(m.route + '/'));
      if (hit && collapsedGroups[g.groupKey]) {
        setCollapsedGroups(prev => {
          const next = { ...prev, [g.groupKey]: false };
          try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(next)); } catch { /* no-op */ }
          return next;
        });
        break;
      }
    }
  }, [location.pathname, items.length, items, collapsedGroups]);

  const platformItems = isPlatformScope ? platformItemsFor(user?.role) : [];

  // Detect macOS for keyboard shortcut badge
  const isMac = useMemo(() => {
    try { return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform || navigator.userAgent); } catch { return false; }
  }, []);

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onMobileClose}
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          data-testid="app-sidebar-backdrop"
        />
      )}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 transition-all duration-200 bg-white dark:bg-background border-r border-border flex flex-col',
          compact ? 'w-16' : 'w-64',
          isMobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0',
        ].join(' ')}
        data-testid="app-sidebar"
      >
        {/* ═══ Header — icon badge + stacked text + collapse toggle ═══ */}
        <div className="h-16 px-3 flex items-center gap-3 border-b border-border shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Circular icon badge */}
            <div className="w-10 h-10 rounded-full bg-surface dark:bg-white/5 flex items-center justify-center shrink-0 overflow-hidden">
              <img
                src="/logos/ankastra-icon.png"
                alt="Ankastra"
                className="w-7 h-7 object-contain"
                draggable={false}
              />
            </div>
            {/* Stacked text — hidden in compact mode */}
            {!compact && (
              <div className="min-w-0">
                <div className="text-lg font-semibold text-foreground leading-tight truncate">
                  Ankastra
                </div>
                <div className="text-[10px] tracking-[0.2em] font-semibold text-foreground-subtle uppercase leading-tight">
                  ENTERPRISE CONSOLE
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setCompact(c => !c)}
            className="p-1.5 rounded-md text-foreground-muted hover:bg-accent shrink-0"
            data-testid="app-sidebar-compact-toggle"
          >
            {compact ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        {/* ═══ Search — hidden in compact mode ═══ */}
        {!compact && (
          <div className="px-3 pt-3 pb-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-foreground-subtle" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Search modules..."
                className="w-full h-9 pl-8 pr-20 rounded-lg border border-border bg-surface dark:bg-white/[0.03] text-sm text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="app-sidebar-search"
              />
              {/* Keyboard shortcut badge */}
              <button
                type="button"
                onClick={onOpenCommandPalette}
                className="absolute right-1.5 top-1.5 h-6 inline-flex items-center gap-0.5 px-1.5 text-[10px] font-mono text-foreground-subtle bg-surface-elevated border border-border rounded shadow-sm hover:bg-surface"
                title="Open command palette"
              >
                <kbd className="text-[10px]">{isMac ? '⌘' : 'Ctrl'}</kbd>
                <kbd className="text-[10px]">K</kbd>
              </button>
            </div>
            {searchResults.length > 0 && (
              <ul className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-border bg-surface-elevated shadow-sm" data-testid="app-sidebar-search-results">
                {searchResults.map((r, idx) => (
                  <li key={r.key}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveSearchIdx(idx)}
                      onClick={() => { setQuery(''); navigate(r.route); onMobileClose(); }}
                      className={[
                        'w-full text-left px-3 py-2 text-sm flex items-center gap-2',
                        activeSearchIdx === idx ? 'bg-primary-subtle text-primary-subtle-foreground' : 'text-foreground hover:bg-accent',
                      ].join(' ')}
                    >
                      <r.Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{r.label}</span>
                      <span className="ml-auto text-[11px] text-foreground-subtle truncate">{r.groupLabel}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ═══ Scrollable nav ═══ */}
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {/* Platform items (SA no-company) */}
          {isPlatformScope && platformItems.length > 0 && (
            <div className="mb-4">
              {!compact && <div className="px-3 mt-2 mb-1 text-[11px] font-semibold tracking-[0.12em] text-foreground-subtle uppercase">Platform</div>}
              <ul className="space-y-0.5">
                {platformItems.map(p => (
                  <NavItem key={p.key} Icon={p.Icon} label={p.label} path={p.path} compact={compact} testid={`sidebar-${p.key}`} />
                ))}
              </ul>
            </div>
          )}

          {/* Pinned section */}
          {pinnedItems.length > 0 && (
            <div className="mb-3" data-testid="app-sidebar-pinned">
              {!compact && (
                <div className="px-3 mt-2 mb-1 text-[11px] font-semibold tracking-[0.12em] text-amber-600 dark:text-amber-400 flex items-center gap-1 uppercase">
                  <Pin className="h-3 w-3" /> Pinned
                </div>
              )}
              <ul className="space-y-0.5">
                {pinnedItems.map(p => (
                  <NavItem
                    key={`pin-${p.key}`}
                    Icon={p.Icon}
                    label={p.label}
                    path={p.route}
                    compact={compact}
                    testid={`sidebar-pin-${p.key}`}
                    onPin={() => togglePin(p.key)}
                    pinned
                    onNavigate={onMobileClose}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Catalog groups (tenant scope) — hidden in platform scope */}
          {!isPlatformScope && items.map(group => {
            const isCollapsed = !!collapsedGroups[group.groupKey];
            return (
              <div key={group.groupKey} className="mb-1">
                {/* Section header — label + chevron, no group icon */}
                <button
                  type="button"
                  onClick={() => toggleGroup(group.groupKey)}
                  className={[
                    'w-full flex items-center px-3 py-2 rounded-md text-foreground-subtle hover:bg-accent',
                    compact ? 'justify-center' : 'justify-between',
                  ].join(' ')}
                  data-testid={`sidebar-group-${group.groupKey}`}
                  aria-expanded={!isCollapsed}
                >
                  {!compact ? (
                    <>
                      <span className="text-[11px] font-semibold tracking-[0.12em] uppercase">{group.groupLabel}</span>
                      <ChevronDown className={[
                        'h-3.5 w-3.5 transition-transform duration-200',
                        isCollapsed ? '-rotate-90' : '',
                      ].join(' ')} />
                    </>
                  ) : (
                    <span className="w-5 h-px bg-border" />
                  )}
                </button>
                {!isCollapsed && (
                  <ul className="mt-0.5 mb-2 space-y-0.5">
                    {group.modules.map(m => (
                      <NavItem
                        key={m.key}
                        Icon={m.Icon}
                        label={m.label}
                        path={m.route}
                        compact={compact}
                        testid={`sidebar-item-${m.key}`}
                        onPin={() => togglePin(m.key)}
                        pinned={pins.includes(m.key)}
                        onNavigate={onMobileClose}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {items.length === 0 && !isPlatformScope && !modLoading && (
            <div className="px-3 py-6 text-xs text-foreground-subtle">
              No modules are active for this company. Ask your administrator to enable modules from Companies → detail → Modules tab.
            </div>
          )}
        </nav>

        {/* ═══ Footer — Settings link + branding ═══ */}
        <div className="border-t border-border shrink-0">
          {/* Settings link */}
          {settingsActive && (
            <NavLink
              to="/settings"
              className={({ isActive }) => [
                'group flex items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-primary-subtle text-primary-subtle-foreground font-medium'
                  : 'text-foreground-muted hover:bg-accent hover:text-foreground',
                compact ? 'justify-center' : '',
              ].join(' ')}
              title={compact ? 'Settings' : undefined}
              data-testid="sidebar-footer-settings"
            >
              <Settings className="h-4 w-4 shrink-0" />
              {!compact && <span className="truncate">Settings</span>}
            </NavLink>
          )}
          {/* Feb 2026 (Production Hotfix) — Advanced Features link.
              The page has existed at /settings/features since Apr 2026
              but had no discoverable sidebar entry, leading admins to
              believe the module was missing. Rendered under the same
              Settings gate so RBAC continues to apply. */}
          {settingsActive && (
            <NavLink
              to="/settings/features"
              className={({ isActive }) => [
                'group flex items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-primary-subtle text-primary-subtle-foreground font-medium'
                  : 'text-foreground-muted hover:bg-accent hover:text-foreground',
                compact ? 'justify-center' : '',
              ].join(' ')}
              title={compact ? 'Advanced Features' : undefined}
              data-testid="sidebar-footer-advanced-features"
            >
              <ArrowUpCircle className="h-4 w-4 shrink-0" />
              {!compact && <span className="truncate">Advanced Features</span>}
            </NavLink>
          )}
          {/* Branding */}
          {!compact && (
            <div className="px-3 py-1.5 text-[10px] text-foreground-subtle">
              Built by Ajinkya Technologies
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// NavItem — a single row. Handles compact / pin hover / active state.
// ────────────────────────────────────────────────────────────────────
function NavItem({ Icon, label, path, compact, testid, onPin, pinned, onNavigate }) {
  return (
    <li className="group/item">
      <NavLink
        to={path}
        end={false}
        onClick={onNavigate}
        className={({ isActive }) => [
          'group flex items-center gap-3 rounded-md text-sm min-h-[36px] px-3 py-2 transition-colors',
          isActive
            ? 'bg-primary-subtle text-primary-subtle-foreground font-medium'
            : 'text-foreground-muted hover:bg-accent hover:text-foreground',
          compact ? 'justify-center' : '',
        ].join(' ')}
        title={compact ? label : undefined}
        data-testid={testid}
      >
        {({ isActive }) => (
          <>
            {Icon ? (
              <Icon className={[
                'h-4 w-4 shrink-0 transition-colors',
                isActive ? 'text-primary' : 'text-foreground-subtle',
              ].join(' ')} />
            ) : null}
            {!compact && <span className="truncate flex-1">{label}</span>}
            {!compact && onPin && (
              <button
                type="button"
                aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
                title={pinned ? 'Unpin' : 'Pin to top'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPin(); }}
                className={[
                  'shrink-0 rounded p-0.5 transition-opacity',
                  pinned
                    ? 'opacity-100 text-primary'
                    : 'opacity-0 group-hover/item:opacity-100 text-foreground-subtle hover:text-primary',
                ].join(' ')}
                data-testid={`${testid}-pin`}
              >
                {pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
              </button>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}
